// React
import React from "react";

// Third party imports
import { useTranslation } from "react-i18next";

import {
  BlockFrame,
  BlockLink,
  DueDate,
  GroupedView,
  itemsOf,
  joinMeta,
  Row,
  RowList,
  statusColor,
  StatusPill,
  Tag,
  useFormatters,
  useFrame,
  useStatusLabel,
  WORK_STATUSES,
} from "./BlockParts";
import { DUE_BUCKETS, dueBucket, groupCount, NONE_KEY } from "./blockUtils";

// Blocks of the work items: tasks, subtasks (of a client and mine), services.
// Props of every block: { data, view, variant, onNavigate }.

const names = (value) => (Array.isArray(value) ? value.filter(Boolean).join(", ") : "");

const DUE_COLORS = {
  overdue: "var(--color-danger)",
  today: "#F57C00",
  tomorrow: "#FFBD2E",
  thisWeek: "var(--color-primary)",
  later: "#9ca3af",
  none: "#d1d5db",
};

// Grouped view (by_*) of a list block, with the rows behind "Mostra elenco"
function Grouped({ items, count, groupKey, view, variant, list, title }) {
  const { t } = useTranslation("aiAssistant");
  const statusLabel = useStatusLabel();
  const isStatus = view === "by_status";
  const isDue = view === "by_due";
  const groups = groupCount(
    items,
    isDue ? (item) => dueBucket(item?.due_date) : groupKey,
    isStatus ? WORK_STATUSES : isDue ? DUE_BUCKETS : undefined
  );
  const noneLabel = t(`blocks.none.${view}`, { defaultValue: t("blocks.none.other") });
  const labelOf = (key) => {
    if (isDue) return t(`blocks.dueBuckets.${key}`);
    if (key === NONE_KEY) return noneLabel;
    return isStatus ? statusLabel(key) : key;
  };
  const colorOf = (key) => {
    if (isDue) return DUE_COLORS[key];
    if (isStatus) return statusColor(key);
    return undefined;
  };
  return (
    <GroupedView
      groups={groups}
      labelOf={labelOf}
      colorOf={colorOf}
      total={items.length}
      count={count}
      // A subtask with two operators counts in both groups
      multi={view === "by_operator"}
      variant={variant}
      label={`${title} · ${t(`blocks.views.${view}`)}`}
      list={list}
    />
  );
}

// ---------------------------------------------------------------------------------------------

export function CompanyTasksBlock({ data, view, variant, onNavigate }) {
  const { t } = useTranslation("aiAssistant");
  const items = itemsOf(data);
  const title = data?.company
    ? t("blocks.titles.list_company_tasks", { company: data.company })
    : t("blocks.titles.tasks");
  const frame = useFrame(data, items, title, view);
  const list = (
    <RowList
      items={items}
      variant={variant}
      renderRow={(task) => (
        <Row
          variant={variant}
          title={task.name}
          href={task.url}
          onNavigate={onNavigate}
          meta={joinMeta(
            task.service,
            names(task.operators),
            typeof task.active_subtasks_count === "number"
              ? t("blocks.activeSubtasks", { count: task.active_subtasks_count })
              : null
          )}
          aside={
            <>
              <StatusPill status={task.status} />
              <DueDate value={task.due_date} done={task.status === "DELIVERED"} />
            </>
          }
        />
      )}
    />
  );
  return (
    <BlockFrame {...frame}>
      {view === "list" ? (
        list
      ) : (
        <Grouped
          items={items}
          count={frame.count}
          view={view}
          variant={variant}
          title={title}
          list={list}
          groupKey={view === "by_service" ? (item) => item?.service : (item) => item?.status}
        />
      )}
    </BlockFrame>
  );
}

export function CompanySubtasksBlock({ data, view, variant, onNavigate }) {
  const { t } = useTranslation("aiAssistant");
  const items = itemsOf(data);
  const title = data?.company
    ? t("blocks.titles.list_company_subtasks", { company: data.company })
    : t("blocks.titles.subtasks");
  const frame = useFrame(data, items, title, view);
  const list = (
    <RowList
      items={items}
      variant={variant}
      renderRow={(subtask) => (
        <Row
          variant={variant}
          title={subtask.name}
          href={subtask.url}
          onNavigate={onNavigate}
          meta={joinMeta(
            subtask.task,
            names(subtask.operators),
            subtask.is_active === false ? t("blocks.inactive") : null
          )}
          extra={
            variant === "page" && Array.isArray(subtask.labels) && subtask.labels.length ? (
              <div className="mt-1.5 flex flex-wrap gap-1">
                {subtask.labels.map((label) => (
                  <Tag key={label}>{label}</Tag>
                ))}
              </div>
            ) : null
          }
          aside={
            <>
              <StatusPill status={subtask.status} />
              <DueDate value={subtask.due_date} done={subtask.status === "DELIVERED"} />
            </>
          }
        />
      )}
    />
  );
  return (
    <BlockFrame {...frame}>
      {view === "list" ? (
        list
      ) : (
        <Grouped
          items={items}
          count={frame.count}
          view={view}
          variant={variant}
          title={title}
          list={list}
          groupKey={view === "by_operator" ? (item) => item?.operators : (item) => item?.status}
        />
      )}
    </BlockFrame>
  );
}

export function MySubtasksBlock({ data, view, variant, onNavigate }) {
  const { t } = useTranslation("aiAssistant");
  const items = itemsOf(data);
  const title = t("blocks.titles.list_my_subtasks");
  const frame = useFrame(data, items, title, view);
  const list = (
    <RowList
      items={items}
      variant={variant}
      renderRow={(subtask) => (
        <Row
          variant={variant}
          title={subtask.name}
          href={subtask.url}
          onNavigate={onNavigate}
          meta={
            subtask.company?.name || subtask.task ? (
              <>
                {subtask.company?.name && (
                  <BlockLink
                    href={subtask.company.url}
                    onNavigate={onNavigate}
                    className="font-medium text-gray-600"
                  >
                    {subtask.company.name}
                  </BlockLink>
                )}
                {subtask.company?.name && subtask.task ? " · " : ""}
                {subtask.task}
              </>
            ) : null
          }
          aside={
            <>
              <StatusPill status={subtask.status} />
              <DueDate value={subtask.due_date} done={subtask.status === "DELIVERED"} />
            </>
          }
        />
      )}
    />
  );
  return (
    <BlockFrame {...frame}>
      {view === "list" ? (
        list
      ) : (
        <Grouped
          items={items}
          count={frame.count}
          view={view}
          variant={variant}
          title={title}
          list={list}
          groupKey={view === "by_company" ? (item) => item?.company?.name : (item) => item?.status}
        />
      )}
    </BlockFrame>
  );
}

export function CompanyServicesBlock({ data, variant, onNavigate }) {
  const { t } = useTranslation("aiAssistant");
  const format = useFormatters();
  const items = itemsOf(data);
  const title = data?.company
    ? t("blocks.titles.list_company_services", { company: data.company })
    : t("blocks.titles.services");
  const frame = useFrame(data, items, title, "list");
  const period = (service) => {
    const start = format.day(service.start_date);
    const end = format.day(service.due_date);
    if (start && end) return `${start} → ${end}`;
    if (start) return t("blocks.from", { date: start });
    if (end) return t("blocks.until", { date: end });
    return null;
  };
  return (
    <BlockFrame {...frame}>
      <RowList
        items={items}
        variant={variant}
        renderRow={(service) => (
          <Row
            variant={variant}
            title={service.name}
            href={service.url}
            onNavigate={onNavigate}
            meta={joinMeta(service.type, period(service), names(service.operators))}
          />
        )}
      />
    </BlockFrame>
  );
}
